import { Directive, ElementRef, HostListener, Input, OnInit, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import {
  appendTypedTimeDigit,
  extractTimeDigits,
  formatPartialTypedTime,
  normalizeTypedTimeInput
} from '../../core/utils/time-input';

/**
 * Mobile/touch web time fields: progressive digit entry with replace-on-first-key.
 * Commits HH:mm on blur only — does not trigger saves (parent saves on dialog dismiss).
 */
@Directive({
  selector: '[appTypedTimeInput]',
  standalone: false
})
export class TypedTimeInputDirective implements OnInit {
  @Input('appTypedTimeInput') enabled = true;

  private digitBuffer = '';
  private valueOnFocus = '';
  private entryStarted = false;

  constructor(
    private readonly el: ElementRef<HTMLInputElement>,
    @Optional() @Self() private readonly ngControl: NgControl
  ) {}

  ngOnInit(): void {
    if (!this.enabled) {
      return;
    }
    const input = this.el.nativeElement;
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');
  }

  private shouldSelectAllOnFocus(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia?.('(pointer: coarse)')?.matches !== true;
  }

  @HostListener('focus')
  onFocus(): void {
    if (!this.enabled) {
      return;
    }
    this.valueOnFocus = this.getValue();
    this.digitBuffer = '';
    this.entryStarted = false;
    if (this.shouldSelectAllOnFocus()) {
      this.scheduleSelectAll();
    }
  }

  @HostListener('click')
  onClick(): void {
    if (!this.enabled || !this.shouldSelectAllOnFocus()) {
      return;
    }
    this.scheduleSelectAll();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.enabled) {
      return;
    }

    const key = event.key;
    if (key === 'Tab' || key === 'Enter' || key.startsWith('Arrow') || event.ctrlKey || event.metaKey) {
      return;
    }

    if (/^\d$/.test(key)) {
      event.preventDefault();
      const input = this.el.nativeElement;
      const replaceAll = !this.entryStarted || this.isAllSelected(input);
      this.digitBuffer = replaceAll ? key : appendTypedTimeDigit(this.digitBuffer, key);
      this.entryStarted = true;
      this.setValue(formatPartialTypedTime(this.digitBuffer));
      return;
    }

    if (key === 'Backspace' || key === 'Delete') {
      event.preventDefault();
      if (!this.entryStarted || this.isAllSelected(this.el.nativeElement)) {
        this.digitBuffer = '';
        this.entryStarted = true;
        this.setValue('');
        return;
      }
      this.digitBuffer = this.digitBuffer.slice(0, -1);
      this.setValue(this.digitBuffer ? formatPartialTypedTime(this.digitBuffer) : '');
    }
  }

  @HostListener('blur')
  onBlur(): void {
    if (!this.enabled || !this.entryStarted) {
      return;
    }

    const raw = this.digitBuffer || extractTimeDigits(this.getValue());
    const normalized = normalizeTypedTimeInput(raw);
    this.digitBuffer = '';
    this.entryStarted = false;

    if (normalized) {
      this.setValue(normalized);
      this.clearInvalidTimeError();
      return;
    }

    if (this.valueOnFocus) {
      this.setValue(this.valueOnFocus);
      this.clearInvalidTimeError();
      return;
    }

    this.setInvalidTimeError();
  }

  private scheduleSelectAll(): void {
    const input = this.el.nativeElement;
    const selectAll = () => {
      if (!input.value) {
        return;
      }
      try {
        input.setSelectionRange(0, input.value.length);
      } catch {
        input.select();
      }
    };
    selectAll();
    setTimeout(selectAll, 0);
  }

  private isAllSelected(input: HTMLInputElement): boolean {
    const len = input.value.length;
    if (!len) {
      return true;
    }
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    return start === 0 && end === len;
  }

  private getValue(): string {
    return String(this.ngControl?.control?.value ?? this.el.nativeElement.value ?? '');
  }

  private setValue(value: string): void {
    if (this.ngControl?.control) {
      this.ngControl.control.setValue(value, { emitEvent: true });
      return;
    }
    this.el.nativeElement.value = value;
  }

  private setInvalidTimeError(): void {
    const control = this.ngControl?.control;
    if (!control) {
      return;
    }
    control.setErrors({ ...(control.errors || {}), invalidTime: true });
    control.markAsTouched();
  }

  private clearInvalidTimeError(): void {
    const control = this.ngControl?.control;
    if (!control?.errors?.['invalidTime']) {
      return;
    }
    const { invalidTime, ...otherErrors } = control.errors;
    control.setErrors(Object.keys(otherErrors).length > 0 ? otherErrors : null);
  }
}
